import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, KeyRound, LogOut } from "lucide-react";

export default function Settings() {
    const [sessions, setSessions] = useState([]);
    const [tfa, setTfa] = useState({ enabled: false });
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [disablePwd, setDisablePwd] = useState("");
    const [disableCode, setDisableCode] = useState("");
    const [doctor, setDoctor] = useState(null);

    async function load() {
        try {
            const [s, t] = await Promise.all([api.get("/me/sessions"), api.get("/me/2fa/status")]);
            setSessions(s.data);
            setTfa(t.data);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load"));
        }
    }
    useEffect(() => {
        api.get("/me/doctor").then((r) => setDoctor(r.data)).catch(() => {});
        load();
    }, []);

    async function revokeOthers() {
        if (!confirm("Log out of all other devices? Your current session stays active.")) return;
        try {
            const r = await api.post("/me/sessions/revoke-others");
            toast.success(`Other sessions revoked. New session_version = ${r.data.session_version}.`);
            load();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }

    async function startSetup() {
        try {
            const r = await api.post("/me/2fa/setup");
            setSetup(r.data);
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }
    async function enableTfa() {
        try {
            await api.post("/me/2fa/enable", { code });
            toast.success("2FA enabled");
            setSetup(null); setCode("");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }
    async function disableTfa() {
        try {
            await api.post("/me/2fa/disable", { password: disablePwd, code: disableCode });
            toast.success("2FA disabled");
            setDisablePwd(""); setDisableCode("");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="settings-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Security</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Settings</h1>
            </div>

            {/* 2FA */}
            <Card className="border-[#123B5D]/10 mb-6" data-testid="settings-2fa-card">
                <CardHeader><CardTitle className="text-sm flex items-center gap-2"><ShieldCheck size={14} /> Two-factor authentication</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                    {tfa.enabled ? (
                        <>
                            <div className="text-sm text-[#15803D] flex items-center gap-2">
                                <ShieldCheck size={16} /> 2FA is <strong>ENABLED</strong>. On next login you&apos;ll be prompted for a TOTP code.
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-end">
                                <div><div className="text-xs text-[#17212B]/60 mb-1">Password</div><Input type="password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} data-testid="tfa-disable-password" /></div>
                                <div><div className="text-xs text-[#17212B]/60 mb-1">Current TOTP code</div><Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} data-testid="tfa-disable-code" /></div>
                            </div>
                            <Button onClick={disableTfa} variant="outline" className="text-[#DC2626] border-[#DC2626]" data-testid="tfa-disable-btn">Disable 2FA</Button>
                        </>
                    ) : setup ? (
                        <>
                            <div className="text-sm text-[#17212B]/70">Scan this QR / URI with any authenticator app (Google Authenticator, Authy, 1Password…):</div>
                            <div className="bg-[#F5F8FA] rounded-lg p-3">
                                <div className="text-xs text-[#17212B]/60">Provisioning URI</div>
                                <code className="text-xs break-all" data-testid="tfa-uri">{setup.provisioning_uri}</code>
                                <div className="text-xs text-[#17212B]/60 mt-2">Or enter secret manually</div>
                                <code className="text-xs" data-testid="tfa-secret">{setup.secret}</code>
                            </div>
                            <div>
                                <div className="text-xs text-[#17212B]/60 mb-1">Enter the 6-digit code from your authenticator</div>
                                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="tfa-enable-code" />
                            </div>
                            <Button onClick={enableTfa} disabled={!code} className="bg-[#0F766E] hover:bg-[#0b5f5a] text-white" data-testid="tfa-enable-btn">Enable 2FA</Button>
                        </>
                    ) : (
                        <>
                            <div className="text-sm text-[#17212B]/70">Add a second layer of protection to your account with a TOTP authenticator app.</div>
                            <Button onClick={startSetup} className="bg-[#123B5D] hover:bg-[#0b2740] text-white" data-testid="tfa-start-setup"><KeyRound size={14} className="mr-2" /> Set up 2FA</Button>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Sessions */}
            <Card className="border-[#123B5D]/10" data-testid="settings-sessions-card">
                <CardHeader className="flex-row items-center justify-between">
                    <CardTitle className="text-sm">Active sessions ({sessions.length})</CardTitle>
                    <Button size="sm" variant="outline" onClick={revokeOthers} data-testid="revoke-others-btn"><LogOut size={12} className="mr-1" /> Log out other sessions</Button>
                </CardHeader>
                <CardContent>
                    {sessions.length === 0 ? (
                        <div className="text-sm text-[#17212B]/60">No tracked sessions.</div>
                    ) : (
                        <ul className="divide-y divide-[#123B5D]/10">
                            {sessions.map((s) => (
                                <li key={s.id} className="py-3 text-sm flex justify-between items-center" data-testid={`session-${s.id}`}>
                                    <div>
                                        <div className="font-mono text-xs text-[#17212B]/50">{s.id.slice(0, 8)}</div>
                                        <div className="text-xs text-[#17212B]/70 mt-0.5 truncate max-w-md">{s.user_agent || "—"}</div>
                                    </div>
                                    <div className="text-xs text-[#17212B]/50">{s.created_at?.slice(0, 19)}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>

            {/* Data Export */}
            <Card className="border-[#123B5D]/10" data-testid="settings-data-export-card">
                <CardHeader>
                    <CardTitle className="text-sm">Download my data</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-[#17212B]/70 mb-3">
                        Download a JSON file containing your profile, content items, uploads
                        metadata, orders, payments and invoices. Nothing else — no other
                        doctors&apos; data, no system secrets.
                    </p>
                    <Button
                        onClick={async () => {
                            const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
                            const tok = localStorage.getItem("ddi_token");
                            const r = await fetch(`${base}/api/v1/me/data-export`,
                                { headers: { Authorization: `Bearer ${tok}` } });
                            if (!r.ok) { alert("Export failed"); return; }
                            const blob = await r.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `doctor-data-export-${Date.now()}.json`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                        }}
                        data-testid="data-export-btn"
                        className="bg-[#123B5D] hover:bg-[#0b2740] text-white"
                    >
                        <LogOut size={12} className="mr-2 rotate-180" /> Download JSON export
                    </Button>
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
